import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessApiService } from "../services/businessApiService";
import {
  type Business,
  type CreateBusinessRequest,
  fromBusinessDto,
  toCreateBusinessDto,
  toUpdateBusinessDto,
  type UpdateBusinessRequest,
} from "../types/businessTypes";

const staleTime = 30 * 60 * 1000; // 30 minutes

export const businessKeys = {
  all: ["business"] as const,
  list: () => [...businessKeys.all, "list"] as const,
  detail: (id: string) => [...businessKeys.all, "detail", id] as const,
  reminderSettings: (id: string) => [...businessKeys.detail(id), "reminder-settings"] as const,
  appointmentRules: (id: string, userId?: string) => [...businessKeys.detail(id), "appointment-rules", userId] as const,
};

export const useBusinesses = () => {
  return useQuery<Business[], Error>({
    queryKey: businessKeys.list(),
    queryFn: async () => {
      const dtos = await businessApiService.getAll();
      return dtos.map((dto) => fromBusinessDto(dto));
    },
    staleTime,
  });
};

export const useBusiness = (id: string) => {
  return useQuery<Business, Error>({
    queryKey: businessKeys.detail(id),
    queryFn: async () => {
      const dto = await businessApiService.getById(id);
      return fromBusinessDto(dto);
    },
    enabled: !!id,
    staleTime,
  });
};

export const useCreateBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation<Business, Error, Partial<CreateBusinessRequest>>({
    mutationFn: async (data: Partial<CreateBusinessRequest>) => {
      const dto = toCreateBusinessDto(data);
      const resultDto = await businessApiService.create(dto);
      return fromBusinessDto(resultDto);
    },
    onSuccess: (business) => {
      queryClient.setQueryData(businessKeys.detail(business.id), business);
      queryClient.setQueryData<Business[]>(businessKeys.list(), (previous) =>
        previous ? [...previous.filter((item) => item.id !== business.id), business] : undefined);
      return queryClient.invalidateQueries({ queryKey: businessKeys.list() });
    },
  });
};

export const useUpdateBusiness = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation<Business, Error, Partial<UpdateBusinessRequest>>({
    mutationFn: async (data: Partial<UpdateBusinessRequest>) => {
      const dto = toUpdateBusinessDto(data);
      const resultDto = await businessApiService.update(id, dto);
      return fromBusinessDto(resultDto);
    },
    onSuccess: (business) => {
      const previous = queryClient.getQueryData<Business>(businessKeys.detail(id))
        ?? queryClient.getQueryData<Business[]>(businessKeys.list())?.find((item) => item.id === id);
      queryClient.setQueryData(businessKeys.detail(id), business);
      queryClient.setQueryData<Business[]>(businessKeys.list(), (previous) =>
        previous?.map((item) => item.id === business.id ? business : item));
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: businessKeys.list() }),
        ...(previous?.timezone !== business.timezone
          ? [queryClient.invalidateQueries({ queryKey: ["availableTimeSlots"] })] : []),
      ]);
    },
  });
};

export const useDeleteBusiness = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error>({
    mutationFn: () => businessApiService.delete(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: businessKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: businessKeys.list() });
    },
  });
};
